import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  message,
  Modal,
  Card,
  Row,
  Col,
  Spin,
  Result,
  Button,
  Space,
  Divider
} from 'antd';

import OrderActions from './components/OrderActions';
import OrderStatusSteps from './components/OrderStatusSteps';
import OrderInfo from './components/OrderInfo';
import CustomerInfo from './components/CustomerInfo';
import OrderItemsTable from './components/OrderItemsTable';
import ShippingTimeline from './components/ShippingTimeline';
import OrderSummary from './components/OrderSummary';
import {
  fetchOrderDetail,
  updateOrderStatus,
  navigateToOrders,
  navigateToEditOrder,
  printOrder
} from './actions';
import './OrderDetail.css';

const OrderDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { orderDetail, loading, error } = useSelector((state) => state.orderDetail);
  const [currentStatus, setCurrentStatus] = useState('Unshipped');

  useEffect(() => {
    if (id) {
      dispatch(fetchOrderDetail(id));
    }
  }, [id, dispatch]);

  useEffect(() => {
    if (orderDetail?.orderStatus) {
      setCurrentStatus(orderDetail.orderStatus);
    }
  }, [orderDetail]);

  const getStatusDisplayName = (status) => {
    switch (status) {
      case 'Unshipped':
        return 'Chưa vận chuyển';
      case 'PartiallyShipped':
        return 'Vận chuyển một phần';
      case 'Shipped':
        return 'Đã vận chuyển';
      case 'Canceled':
        return 'Đã hủy';
      default:
        return status;
    }
  };

  const handleStatusChange = (status) => {
    Modal.confirm({
      title: 'Cập nhật trạng thái đơn hàng',
      content: `Bạn có chắc chắn muốn cập nhật trạng thái đơn hàng này thành "${getStatusDisplayName(status)}"?`,
      okText: 'Cập nhật',
      cancelText: 'Hủy',
      onOk: async () => {
        try {
          const result = await dispatch(updateOrderStatus({ id: orderDetail.id, status }));
          if (result.success) {
            setCurrentStatus(status);
            message.success('Cập nhật trạng thái đơn hàng thành công');
          } else {
            message.error('Có lỗi xảy ra khi cập nhật trạng thái đơn hàng');
          }
        } catch (error) {
          message.error('Có lỗi xảy ra khi cập nhật trạng thái đơn hàng');
        }
      }
    });
  };

  const handleBack = () => {
    dispatch(navigateToOrders(navigate));
  };

  const handleEdit = () => {
    dispatch(navigateToEditOrder(id, navigate));
  };

  const handlePrint = () => {
    dispatch(printOrder());
  };

  if (loading) {
    return (
      <div className="order-detail-loading" style={{
        height: '400px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
      }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <Result
        status="error"
        title="Load Order Failed"
        subTitle={error}
        extra={
          <Button type="primary" onClick={handleBack}>
            Back to Orders
          </Button>
        }
      />
    );
  }

  if (!orderDetail) {
    return (
      <Result
        status="404"
        title="Order Not Found"
        subTitle="The order you're looking for doesn't exist."
        extra={
          <Button type="primary" onClick={handleBack}>
            Back to Orders
          </Button>
        }
      />
    );
  }

  return (
    <div className="order-detail-container">
      {/* Header Actions */}
      <OrderActions
        orderNumber={orderDetail.amazonOrderId}
        onBack={handleBack}
        onPrint={handlePrint}
        onEdit={handleEdit}
      />

      {/* Status Steps */}
      <OrderStatusSteps
        currentStatus={currentStatus}
        onStatusChange={handleStatusChange}
      />

      {/* Main Content */}
      <Row gutter={[24, 24]}>
        {/* Left Column */}
        <Col xs={24} lg={18}>
          {/* Order Summary */}
          <OrderSummary order={orderDetail} />

          {/* Order Items */}
          <OrderItemsTable order={orderDetail} />

          {/* Shipping Timeline */}
          <ShippingTimeline order={orderDetail} />
        </Col>

        {/* Right Column */}
        <Col xs={24} lg={6}>
          {/* Order Info */}
          <OrderInfo order={orderDetail} />

          {/* Customer Info */}
          <CustomerInfo order={orderDetail} />

          {/* Notes */}
          {orderDetail.notes && (
            <Card title="Notes" style={{ marginTop: 16 }}>
              <p style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                {orderDetail.notes}
              </p>
            </Card>
          )}
        </Col>
      </Row>
    </div>
  );
};

export default OrderDetail; 